import React from 'react'
import {Typography} from '@mui/material'
import moment from 'moment'
import {ColumnDef} from 'src/components-new/data/DataTable'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {ColumnConfig, getDigitalEnrollmentStateColor, getDigitalEnrollmentStateLabel} from './enrollmentHubOptions'

export interface UnitValue {
  value?: number
  unitType?: string
}

export interface DigitalEnrollmentRow {
  _id: string
  firstName?: string
  lastName?: string
  email?: string
  cellPhoneNumber?: string
  serviceType?: string
  therapyType?: {_id: string, name: string}
  therapyTypeId?: string
  drugDosage?: UnitValue
  dailyDosage?: UnitValue
  speedOfAssumption?: UnitValue
  speedOfDevice?: UnitValue
  customFields?: Array<{id: string, value: any}>
  state?: string
  createdAt?: string
  [key: string]: any
}

// Same value/label pairs as the legacy page
// (src/pages/digitalEnrollment/DigitalEnrollment.tsx lines 47-70).
export const DEFAULT_COLUMNS: ColumnConfig[] = [
  {id: 'lastName', label: 'Cognome Paziente'},
  {id: 'firstName', label: 'Nome Paziente'},
  {id: 'therapyName', label: 'Terapia'},
  {id: 'createdAt', label: 'DATA CREAZIONE'},
  {id: 'state', label: 'Stato'},
]

function renderCell(id: string) {
  return (row: DigitalEnrollmentRow): React.ReactNode => {
    if (id === 'therapyName') {
      return <Typography variant="body2">{row.therapyType?.name ?? '—'}</Typography>
    }
    if (id === 'createdAt') {
      return <Typography variant="body2">{row.createdAt ? moment(row.createdAt).format('ddd DD MMM YYYY HH:mm') : '—'}</Typography>
    }
    if (id === 'state') {
      return <StatusPill label={getDigitalEnrollmentStateLabel(row.state)} color={getDigitalEnrollmentStateColor(row.state)} />
    }
    return <Typography variant="body2">{(row as any)[id] ?? '—'}</Typography>
  }
}

/**
 * Columns are PSP-driven (`enrollmentHubConfigurations.digitalEnrollmentConfigurations.columnOptions`),
 * falling back to a sensible default set — mirrors the pattern in
 * echocardiographicRequestsColumns.tsx. `state` renders as the mobile card's top-right
 * badge; there's no `mobileCardHeader` column, so the header stays limited to that badge
 * and `lastName` renders as an ordinary labeled field in the card body (see DataTable's
 * mobile-card fallback when no column claims the header slot).
 * `therapyName` is derived from `therapyType.name` (was the legacy `cleanData` mapping)
 * so it isn't sortable server-side.
 */
export function buildColumns(columnConfig?: ColumnConfig[]): ColumnDef<DigitalEnrollmentRow>[] {
  const config = columnConfig && columnConfig.length > 0 ? columnConfig : DEFAULT_COLUMNS
  return config.map((col, index) => ({
    key: `${col.id}-${index}`,
    header: col.label,
    mobileCardBadge: col.id === 'state',
    sortKey: col.id === 'therapyName' ? undefined : col.id,
    render: renderCell(col.id),
  }))
}
